/**
 * The keys of a filter that hold a multi-select group, i.e. a list of selected
 * keys. Lets `toggleFilterGroup` work on a domain filter interface (which has
 * no index signature) while still rejecting a non-group key.
 */
type FilterGroupKey<F> = {
	[K in keyof F]-?: F[K] extends string[] | undefined ? K : never;
}[keyof F];

/**
 * Adds or removes a value in one filter group.
 *
 * Groups mean OR within a group and AND across groups, matching what the
 * backend applies. An emptied group is dropped rather than left as `[]`, so an
 * unconstrained filter serialises as `{}` and the backend persists no filter
 * at all.
 */
export function toggleFilterGroup<F extends object, K extends FilterGroupKey<F>>(
	filter: F,
	group: K,
	key: string
): F {
	const current = (filter[group] as string[] | undefined) ?? [];
	const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];

	const updated: F = { ...filter, [group]: next };

	if (!next.length) delete updated[group];

	return updated;
}

/** True when no group carries a constraint. */
export function isEmptyFilter(filter: object) {
	return Object.values(filter).every((group) => !(group as string[] | undefined)?.length);
}
